import { memo, useEffect, useMemo, useRef, type MutableRefObject } from 'react'
import type { AssistantMessage, ToolCall } from '../../../../types'
import { useIsChatAwaitingNextDrain } from '../../../../store'
import { MessageEditable } from './message-editable'
import { buildRenderParts } from '../tools/build-render-parts'
import { ToolCallsProvider } from '../tools/tool-calls-context'
import { useInterleavedParts, type GroupContentPart } from '../hooks/use-interleaved-parts'
import { areAssistantBodyPropsEqual } from '../comparators'
import { isTopLevelToolCall } from '../tools/tool-call-utils'
import { StreamingLoader } from './loader'
import { ToolCallList } from './tool-call-list'
import { FinalMessageReveal } from './final-reveal'
import { didConversationStream, markConversationStreamed } from './reveal-once'
import { useAnswerReveals } from '../hooks/use-answer-reveals'
import { joinAnswerText } from '../hooks/join-answer-text'

// ---------------------------------------------------------------------------
// AssistantBody — the assistant turn's parts orchestration
// ---------------------------------------------------------------------------

export { buildRenderParts }

/** Edit-mode plumbing, present only where the actions row can enter edit mode (primary rows). */
export type AssistantBodyEditing = {
  isEditMode: boolean
  formattedContent: string
  editedTextRef: MutableRefObject<string>
  onSaveEdit: () => void
  onCancelEdit: () => void
}

export interface AssistantBodyProps {
  message: AssistantMessage
  isMostRecent?: boolean
  /** Whether this chat's turn is live (loading, streaming, or running tools). */
  isTurnActive: boolean
  /** Omitted on submessage rows — their actions row is hidden, so edit mode is unreachable. */
  editing?: AssistantBodyEditing
  // Mutable by contract: FinalMessageReveal assigns the rendered container into it.
  renderedContentRef?: MutableRefObject<HTMLDivElement | null>
  groupToolCalls?: Array<ToolCall>
  groupContentParts?: Array<GroupContentPart>
  thinkingContent?: string
}

export const AssistantBody = memo(
  ({
    message,
    isMostRecent,
    isTurnActive,
    editing,
    renderedContentRef,
    groupToolCalls,
    groupContentParts,
    thinkingContent,
  }: AssistantBodyProps) => {
    // -----------------------------------------------------------------------
    // Tool call merging
    // -----------------------------------------------------------------------

    const renderParts = useMemo(
      () => buildRenderParts(message),
      [message.content, message.toolCalls, message.contentParts],
    )

    // Every tool call the turn can reference (the message's own win over the
    // group's on id collision), sub-level included so nested subagent calls
    // resolve. Reference-stable by construction: the store's patch layer
    // (`sink-patches.ts`) replaces exactly the changed tool call and keeps
    // every other element's identity, so this map — and the context value the
    // provider hands out below — only changes when a tool call actually did.
    const allToolCallMapRef = useRef(new Map<string, ToolCall>())
    const allToolCallMap = useMemo(() => {
      const map = new Map<string, ToolCall>()
      for (const tc of message.toolCalls ?? []) map.set(tc.id, tc)
      for (const tc of groupToolCalls ?? []) {
        if (!map.has(tc.id)) map.set(tc.id, tc)
      }
      return map
    }, [message.toolCalls, groupToolCalls])
    allToolCallMapRef.current = allToolCallMap

    // -----------------------------------------------------------------------
    // Hooks — interleaving, entries, view-more, scroll
    // -----------------------------------------------------------------------

    const { interleavedParts, trailingTextParts } = useInterleavedParts({
      renderParts,
      groupContentParts,
      groupToolCalls,
      allToolCallMapRef,
      thinkingContent,
      messageId: message.id ?? undefined,
      // Swap-stable identity keys this message's own parts (parts/registry
      // contract); 'msg' is the no-identity fallback, unique within one body.
      sourceId: message.clientId ?? message.id ?? 'msg',
    })

    const isStreaming = !!(isMostRecent && isTurnActive)

    // -----------------------------------------------------------------------
    // Final reveal — trailing text displays after stream ends
    // -----------------------------------------------------------------------

    // Track whether we ever saw streaming in this mount; gates the reveal so
    // history loads render instantly while just-streamed messages animate.
    // Also marked at conversation level (session-scoped) so a row that
    // remounts between streaming and turn end still reveals — the per-message
    // watermark in reveal-once.ts is what keeps a reveal from ever replaying.
    const hasStreamedRef = useRef(false)
    useEffect(() => {
      if (isStreaming) {
        hasStreamedRef.current = true
        markConversationStreamed(message.conversation_id)
      }
    }, [isStreaming, message.conversation_id])

    // Only the trailing text (after the last tool call) is the final answer.
    // Pre-tool text is already rendered inside ToolCallList — using
    // message.content here would duplicate it.
    const finalText = useMemo(() => joinAnswerText(trailingTextParts), [trailingTextParts])
    const { toolListParts, answers, shouldAnimate } = useAnswerReveals({
      renderParts,
      interleavedParts,
      trailingTextParts,
      finalText,
      isStreaming,
      messageId: message.id,
      animatesAfterStream: hasStreamedRef.current || (!!isMostRecent && didConversationStream(message.conversation_id)),
    })

    // -----------------------------------------------------------------------
    // Reveal bookkeeping
    // -----------------------------------------------------------------------

    const revealedToolIdsRef = useRef(new Set<string>())
    const justRevealedRef = useRef(new Set<string>())

    const revealedThinkingCountRef = useRef(0)
    useEffect(() => {
      if (!isMostRecent) return

      // Reveal thinking parts
      const thinkingParts = interleavedParts.filter((p) => p.type === 'thinking')
      if (thinkingParts.length > revealedThinkingCountRef.current) {
        for (let i = revealedThinkingCountRef.current; i < thinkingParts.length; i++) {
          revealedToolIdsRef.current.add(thinkingParts[i].stableKey)
        }
        revealedThinkingCountRef.current = thinkingParts.length
      }

      // Reveal completed tools
      for (const toolCall of allToolCallMapRef.current.values()) {
        if (!isTopLevelToolCall(toolCall)) continue
        if (
          (toolCall.status === 'success' || toolCall.status === 'failed') &&
          !revealedToolIdsRef.current.has(toolCall.id)
        ) {
          revealedToolIdsRef.current.add(toolCall.id)
          justRevealedRef.current.add(toolCall.id)
          setTimeout(() => justRevealedRef.current.delete(toolCall.id), 800)
        }
      }
    }, [toolListParts, isMostRecent])

    // -----------------------------------------------------------------------
    // Derived state
    // -----------------------------------------------------------------------

    // Hide the loader only during the brief between-turns drain handoff so it
    // doesn't pop in before the next assistant placeholder appears. Don't gate
    // on the whole "queue has items" state — we want the loader to keep
    // showing on the actively-streaming bubble even when queued items wait
    // behind it. only the most-recent message consults the gate.
    const isAwaitingNextDrain = useIsChatAwaitingNextDrain(isMostRecent ? message.conversation_id : undefined)
    const showLoader = !!(isMostRecent && isTurnActive && !isAwaitingNextDrain)

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    if (editing?.isEditMode) {
      return (
        <MessageEditable
          id={message.id}
          initialHTML={editing.formattedContent}
          className=""
          onChange={(text) => {
            editing.editedTextRef.current = text
          }}
          onSubmit={editing.onSaveEdit}
          onCancel={editing.onCancelEdit}
        />
      )
    }

    return (
      <>
        {toolListParts.length > 0 && (
          <div className="flex w-full flex-col">
            <ToolCallsProvider value={allToolCallMap}>
              <ToolCallList
                parts={toolListParts}
                isStreaming={isStreaming}
                justRevealedRef={justRevealedRef}
                messageId={message.id}
                conversationId={message.conversation_id}
              />
            </ToolCallsProvider>
          </div>
        )}

        {answers.map((answer) => (
          <FinalMessageReveal
            key={answer.key}
            text={answer.text}
            messageId={message.id}
            revealKey={answer.revealKey}
            shouldAnimate={shouldAnimate}
            renderedContentRef={answer.isLive ? renderedContentRef : undefined}
          />
        ))}

        <StreamingLoader show={showLoader} conversationId={message.conversation_id} />
      </>
    )
  },
  areAssistantBodyPropsEqual,
)
AssistantBody.displayName = 'AssistantBody'
